import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Slider } from "@/components/ui/slider";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Play, Pause, RotateCcw, RotateCw, Volume2, VolumeX, Maximize, Gauge, Captions, CaptionsOff } from "lucide-react";

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];
const SPEED_KEY = "kohc_video_speed";
const CC_KEY = "kohc_video_captions";
const fmt = (s) => { if (!Number.isFinite(s)) return "0:00"; const m = Math.floor(s / 60); const sec = Math.floor(s % 60); return `${m}:${String(sec).padStart(2, "0")}`; };
const ctl = "inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-full text-white hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-coral transition-colors";

function CtlButton({ label, onClick, testId, children }) {
  return <button type="button" aria-label={label} title={label} data-testid={testId} onClick={(e) => { e.stopPropagation(); onClick(); }} className={ctl}>{children}</button>;
}

export function VideoPlayer({ src, poster, captions, lessonKey, onDuration, onProgress }) {
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const hideTimer = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [speed, setSpeed] = useState(() => Number(localStorage.getItem(SPEED_KEY)) || 1);
  const [visible, setVisible] = useState(true);
  const [cc, setCc] = useState(() => localStorage.getItem(CC_KEY) !== "off");

  const v = () => videoRef.current;
  const applyCc = useCallback((on) => { const t = v()?.textTracks?.[0]; if (t) t.mode = on ? "showing" : "hidden"; }, []);
  useEffect(() => { applyCc(cc); localStorage.setItem(CC_KEY, cc ? "on" : "off"); }, [cc, lessonKey, applyCc]);

  // Browsers silently drop cross-origin <track> files, so always load captions as a same-origin blob URL.
  const [trackSrc, setTrackSrc] = useState(null);
  useEffect(() => {
    let url = null, cancelled = false;
    setTrackSrc(null);
    if (!captions) return undefined;
    const path = captions.includes("/api/") ? captions.slice(captions.indexOf("/api/") + 4) : captions;
    api.get(path, { responseType: "text", transformResponse: [(d) => d] })
      .then((r) => { if (cancelled) return; url = URL.createObjectURL(new Blob([r.data], { type: "text/vtt" })); setTrackSrc(url); })
      .catch(() => { if (!cancelled) setTrackSrc(captions); });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [captions, lessonKey]);
  const toggleCc = () => { setCc((x) => !x); showControls(); };
  const showControls = useCallback(() => {
    setVisible(true);
    clearTimeout(hideTimer.current);
    if (v() && !v().paused) hideTimer.current = setTimeout(() => setVisible(false), 3000);
  }, []);

  useEffect(() => { if (v()) v().playbackRate = speed; localStorage.setItem(SPEED_KEY, String(speed)); }, [speed, lessonKey]);
  useEffect(() => () => clearTimeout(hideTimer.current), []);

  const toggle = () => { const el = v(); if (!el) return; if (el.paused) el.play(); else el.pause(); showControls(); };
  const skip = (d) => { const el = v(); if (!el) return; el.currentTime = Math.min(Math.max(0, el.currentTime + d), el.duration || 0); showControls(); };
  const seek = ([val]) => { const el = v(); if (el) { el.currentTime = val; setTime(val); } showControls(); };
  const toggleMute = () => { const el = v(); if (!el) return; el.muted = !el.muted; setMuted(el.muted); showControls(); };
  const changeVolume = ([val]) => { const el = v(); if (!el) return; el.volume = val; el.muted = val === 0; setVolume(val); setMuted(el.muted); };
  const fullscreen = () => { const el = wrapRef.current; if (!el) return; if (document.fullscreenElement) document.exitFullscreen(); else el.requestFullscreen?.(); showControls(); };
  const onKey = (e) => {
    if (e.target.tagName === "INPUT" || e.target.getAttribute("role") === "slider") return;
    if (e.code === "Space") { e.preventDefault(); toggle(); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); skip(-10); }
    else if (e.key === "ArrowRight") { e.preventDefault(); skip(10); }
    else if (e.key.toLowerCase() === "f") { e.preventDefault(); fullscreen(); }
  };
  const onMeta = (e) => { const d = e.target.duration; setDuration(d); e.target.playbackRate = speed; onDuration?.(d); applyCc(cc); };

  return (
    <div ref={wrapRef} tabIndex={0} data-testid="video-player" onKeyDown={onKey} onMouseMove={showControls} onTouchStart={showControls}
      className={`relative w-full bg-black outline-none focus-visible:ring-2 focus-visible:ring-coral group ${visible ? "" : "sm:cursor-none"}`}>
      <div className="relative aspect-video">
      <video ref={videoRef} key={lessonKey} data-testid="lesson-video" className="w-full h-full kohc-video" poster={poster} playsInline onClick={toggle}
        onPlay={() => { setPlaying(true); showControls(); }} onPause={() => { setPlaying(false); setVisible(true); clearTimeout(hideTimer.current); }}
        onTimeUpdate={(e) => { setTime(e.target.currentTime); if (e.target.duration) onProgress?.(e.target.currentTime / e.target.duration); }} onLoadedMetadata={onMeta} onEnded={() => { setPlaying(false); onProgress?.(1); }}>
        <source src={src} type="video/mp4" />
        {trackSrc && <track kind="captions" src={trackSrc} srcLang="en" label="English" default={cc} onLoad={() => applyCc(cc)} data-testid="lesson-captions-track" />}
      </video>

      {!playing && (
        <button type="button" aria-label="Play" data-testid="video-center-play" onClick={toggle}
          className="absolute inset-0 m-auto h-20 w-20 rounded-full bg-coral/90 hover:bg-coral text-white grid place-items-center shadow-lg transition-transform hover:scale-105 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/60">
          <Play className="h-9 w-9 ml-1" fill="currentColor" />
        </button>
      )}
      </div>

      <div data-testid="video-controls" onClick={(e) => e.stopPropagation()}
        className={`px-3 pb-2 pt-2 bg-black sm:absolute sm:inset-x-0 sm:bottom-0 sm:pt-10 sm:bg-transparent sm:bg-gradient-to-t sm:from-black/85 sm:via-black/40 sm:to-transparent transition-opacity duration-300 ${visible ? "opacity-100" : "sm:opacity-0 sm:pointer-events-none"}`}>
        <div className="px-1 mb-1">
          <Slider data-testid="video-seek" value={[Math.min(time, duration || 0)]} max={duration || 0} step={0.1} onValueChange={seek} aria-label="Seek"
            className="[&_[role=slider]]:h-4 [&_[role=slider]]:w-4 [&_[role=slider]]:border-coral [&_[role=slider]]:bg-coral [&_.bg-primary]:bg-coral [&_.bg-secondary]:bg-white/30" />
        </div>
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
          <CtlButton label={playing ? "Pause" : "Play"} onClick={toggle} testId="video-play-btn">{playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}</CtlButton>
          <CtlButton label="Back 10 seconds" onClick={() => skip(-10)} testId="video-back-btn"><RotateCcw className="h-5 w-5" /></CtlButton>
          <CtlButton label="Forward 10 seconds" onClick={() => skip(10)} testId="video-fwd-btn"><RotateCw className="h-5 w-5" /></CtlButton>
          <span data-testid="video-time" className="text-xs text-white/90 tabular-nums px-2 min-h-[44px] inline-flex items-center">{fmt(time)} / {fmt(duration)}</span>
          <div className="ml-auto flex items-center gap-x-1">
            <div className="flex items-center">
              <CtlButton label={muted ? "Unmute" : "Mute"} onClick={toggleMute} testId="video-mute-btn">{muted || volume === 0 ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}</CtlButton>
              <div className="hidden sm:block w-20 px-1">
                <Slider data-testid="video-volume" value={[muted ? 0 : volume]} max={1} step={0.05} onValueChange={changeVolume} aria-label="Volume"
                  className="[&_[role=slider]]:h-3.5 [&_[role=slider]]:w-3.5 [&_[role=slider]]:border-white [&_[role=slider]]:bg-white [&_.bg-primary]:bg-white [&_.bg-secondary]:bg-white/30" />
              </div>
            </div>
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <button type="button" aria-label="Playback speed" data-testid="video-speed-btn" className={`${ctl} px-2 gap-1 text-xs font-semibold`}><Gauge className="h-5 w-5" />{speed}×</button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[7rem]">
                {SPEEDS.map((s) => (
                  <DropdownMenuItem key={s} data-testid={`video-speed-${s}`} onSelect={() => { setSpeed(s); if (v()) v().playbackRate = s; }} className={s === speed ? "font-bold text-brand" : ""}>{s}×{s === 1 ? " (normal)" : ""}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {captions && (
              <CtlButton label={cc ? "Hide captions" : "Show captions"} onClick={toggleCc} testId="video-cc-btn">
                {cc ? <Captions className="h-5 w-5 text-coral" /> : <CaptionsOff className="h-5 w-5" />}
              </CtlButton>
            )}
            <CtlButton label="Fullscreen" onClick={fullscreen} testId="video-fullscreen-btn"><Maximize className="h-5 w-5" /></CtlButton>
          </div>
        </div>
      </div>
    </div>
  );
}
